// BLE bring-up screen — Super Admin only, mobile only.
//
// WHY THIS EXISTS. The BLE contract has four steps that all fail SILENTLY:
// a truncated write, a passive scan, an overlapping GATT call, a key mismatch.
// Every one of them presents as "nothing happened". Without the raw exchange in
// front of you, the only way to tell them apart is a laptop, a USB cable and
// nRF Connect — which is not available standing next to a switch on a wall.
//
// So this shows what was scanned, what went out, and what came back, verbatim.
// It is the app-side equivalent of §11's nRF Connect walkthrough, and it is the
// first thing to open when a device does not respond.
//
// Deliberately raw: no friendly errors, no retries, no interpretation. During
// bring-up, "AUTH FAIL" is more useful than "could not connect".
//
// ── The panel and the Wi-Fi form ───────────────────────────────────────────
// Two things sit above the log, and both are still the raw exchange underneath.
//
//   THE PANEL. Once the device says what it is, the real switch buttons appear —
//   the same RelayTile / AllBar the 4-touch screen draws. Typing "3-1" into a
//   probe button tests the transport; pressing the button tests what the product
//   will actually do, in the order the buttons are physically arranged. The type
//   comes from what the device says (core/ble/bleIdentity) and is overridable,
//   because the INFO reply format is not frozen and a guess can be wrong.
//
//   THE WI-FI FORM. Provisioning is the one thing that MUST work before a device
//   is any use, and until now it could only be exercised through onboarding —
//   which consumes the state it needs and cannot be repeated. Here it goes out
//   on the link that is already open, with the write and the reply in the log.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, List, ListItemButton, ListItemText, Divider,
  CircularProgress, Chip, IconButton, Menu, MenuItem,
  ListItemIcon, useTheme, Checkbox,
} from '@mui/material';
import WifiIcon from '@mui/icons-material/Wifi';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TuneIcon from '@mui/icons-material/Tune';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CenterTitleBar } from '../components/MobileAppBar';
import { interceptBack } from '../../../core/nav/backIntercept';
import { useApp } from '../../../core/state/AppContext';
import {
  GATEWAY_DEVICE_ID, fetchGatewayNetwork, isMeshNetOk, isMeshNetRejected, maskMeshNet, meshNetCommand,
} from '../../../core/ble/mesh/gatewayKeys';
import { copyText } from '../../../core/utils/clipboard';
import {
  bleSupported, scanForDevices, stopScan,
  isBleEnabled, requestBleEnable, watchBleEnabled, openBleSettings,
} from '../../../core/ble/bleClient';
import { openSession } from '../../../core/ble/bleSession';
import {
  adoptControlLink, relayBleStatus, closeControlLink, onBleTrace,
} from '../../../core/ble/bleControl';
import { pickBenchType, benchTypeNote } from '../../../core/ble/benchType';
import { AddWifiDialog } from '../../shared/DeviceWifiDialogs';
import { chooseWifiPayload } from '../../../core/ble/bleConfig';
import { setForcedBle } from '../../../core/ble/bleForced';
import { getDeviceDetailFresh } from '../../../core/api/api';
import {
  CMD_PING, CMD_STATUS, CMD_INFO, CMD_WHOAMI,
  isProvisionFailure, isProvisionSuccess,
} from '../../../core/ble/bleConfig';
import { deviceKey, toHex, selfTest, DEFAULT_KEY_VERSION } from '../../../core/ble/bleAuth';
import { channelCountFor, identityRank, learnFromNotification } from '../../../core/ble/bleIdentity';
import { parseStatus, channelsFor } from '../../../core/devices/statusFormat';
import { deviceLabel } from '../../../core/devices/deviceMeta';
import BleDevicePanel from '../components/BleDevicePanel';
import BluetoothOffBanner from '../components/BluetoothOffBanner';
import BleWifiForm from '../components/BleWifiForm';
import BleBatchPanel from '../components/BleBatchPanel';
import { useServerDetails, serverSummary, BleServerInfoDialog } from '../components/BleServerInfo';
import BleInstallPanel from '../components/BleInstallPanel';

// Commands worth having as one tap during bring-up. PING is first because it is
// the only one allowed BEFORE auth — if PING answers and AUTH does not, the
// radio is fine and the problem is the key.
const PROBES = [CMD_PING, CMD_STATUS, CMD_INFO, CMD_WHOAMI, '1-1', '1-0', 'ALLOFF'];

// Manual type override. Every product whose panel this screen can draw, so a
// device that does not report its type — or reports one that is wrong — can
// still be exercised through the real buttons.
// ⚠️ NOT ONLY SWITCH BANKS (2026-10-25). A board with no database record still
// has to be exercised as what it IS, and until now the only shapes on offer were
// panels of switches — so a curtain or a lock could not be driven from here at
// all. The server's DeviceID is the normal answer (pickBenchType); this list is
// the fallback for a fresh piece and the override for a record that is wrong.
const TYPE_CHOICES = [
  { id: 55, label: '1' },
  { id: 56, label: '2' },
  { id: 49, label: '4' },
  { id: 92, label: '4+1' },
  { id: 93, label: '7+1' },
  { id: 87, label: '7+2' },
  { id: 53, label: 'Fan' },
  { id: 13, label: 'Curtain' },
  { id: 37, label: 'Gate' },
  { id: 102, label: 'Lock' },
  { id: 25, label: 'WWH' },
  { id: 24, label: 'RGB' },
  { id: 23, label: 'WWRGB' },
];

// Long enough for a busy phone, short enough that a dead device does not hold
// the form. Matches provisionWifi's own budget.
const WIFI_REPLY_TIMEOUT_MS = 12000;

// ── Proving a press all the way to the relay ───────────────────────────────
//
// A button press crosses TWO boundaries, and only one of them is visible from
// the phone:
//
//   phone → device   the GATT write. Its promise resolving means the bytes
//                    left; that is all it means.
//   device → MCU     BLEHelper hands the command to tuya_mcu_handle_switch_command,
//                    which drives a Tuya DP over the UART. Nothing about that
//                    reaches us directly.
//
// The only evidence the second hop happened is the STATUS FRAME THE DEVICE
// PUSHES BACK once the MCU reports its new state. So a press is "sent" when the
// write resolves and "confirmed" when that frame lands — and the firmware calls
// tuya_mcu_set_source("BLE") before dispatching, so the `#BLE` tag on the echo
// says the change came from OUR command rather than someone touching the panel
// at the same moment.
//
// No echo inside this window means the write reached a device that did not act
// on it: an unmapped channel, a Tuya UART that is not answering, or a command
// the parser did not recognise. All three look identical without this.
const ACK_TIMEOUT_MS = 3000;

// Which commands produce a status frame. INFO and WHOAMI answer with their own
// line and no status, so arming the ack for them would report a false timeout.
const expectsStatus = (cmd) => /^\d+-\d+$/.test(cmd)
  || /^(ALLON|ALLOFF|STATUS)$/i.test(String(cmd).trim());

const MONO = { fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 11, lineHeight: 1.45 };

// Where the type came from, in words. On a bench "type 49" is only half the
// answer — whether the device SAID so or the app worked it out from a status
// width is what decides whether to believe it.
const SOURCE_WORDS = {
  info: 'INFO reply',
  stated: 'reported by device',
  channels: 'guessed from channel count',
};

// The two hops of a press, in one line. Colours match the log: blue for a write
// that went out, green for one the device confirmed, red for one it did not.
function AckLine({ ack, c }) {
  const map = {
    sent: {
      colour: '#4FC3F7',
      text: `→ ${ack.cmd} written — waiting for the device to report back…`,
    },
    confirmed: {
      colour: '#81C784',
      text: `✓ ${ack.cmd} — MCU applied it, status back in ${ack.ms} ms (source ${ack.source})`,
    },
    'no-echo': {
      colour: '#EF5350',
      text: `✗ ${ack.cmd} written, no status in ${ACK_TIMEOUT_MS / 1000} s — `
        + 'the device took the bytes but the MCU did not report a change',
    },
    'write-failed': {
      colour: '#EF5350',
      text: `✗ ${ack.cmd} — the write itself failed; nothing reached the device`,
    },
  };
  const v = map[ack.state];
  if (!v) return null;
  return (
    <Typography sx={{
      color: v.colour,
      fontSize: 11,
      fontWeight: 700,
      px: 1.5,
      py: 0.5,
      textAlign: 'center',
      borderTop: `1px solid ${c.text}22`,
    }}
    >
      {v.text}
    </Typography>
  );
}

function identityLabel(identity) {
  const src = SOURCE_WORDS[identity.via] || identity.via;
  const fw = identity.firmware != null ? ` · fw ${identity.firmware}` : '';
  return `type ${identity.deviceId} · ${identity.channels ?? '?'} ch (${src})${fw}`;
}

// ⚠️ A WRAPPER, so the permission check sits OUTSIDE every hook this screen
// owns. The bench holds ~15 useState calls and effects that start scanning and
// open a BLE session on mount; an `if (!isSuperAdmin) return` placed among them
// would be a conditional hook, and placed after all of them it would still have
// run the effects — a non-admin would have driven the radio before being told
// no. Splitting the component means the bench never mounts at all.
//
// This is the third layer, not the only one: the route gates too (MobileRoutes)
// and MeTab does not draw the tile. It was previously the layer that did not
// exist — the sole one of the four admin screens with no check of its own.
export default function BleTestScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const { isSuperAdmin } = useApp();

  if (!isSuperAdmin) {
    return (
      <Box sx={{ height: '100%', bgcolor: c.back }}>
        <CenterTitleBar title="BLE Test" showBack />
        <Typography sx={{ color: c.text, p: 3, textAlign: 'center' }}>
          This screen is for administrators.
        </Typography>
      </Box>
    );
  }
  return <BleTestBench />;
}

// ⚠️ THE SCAN LIST OUTLIVES THE SCREEN. "Open full screen" leaves this route,
// and coming back remounted it with an EMPTY list — the boards found a minute
// ago had to be scanned again (owner, 2026-10-18: back must return to the same
// page). Kept for the app session only; a new Scan replaces it.
let lastScan = { devices: [], installMode: false };

function BleTestBench() {
  const theme = useTheme();
  const c = theme.sli;
  const { userId } = useApp();

  const [devices, setDevices] = useState(() => lastScan.devices);
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);
  // ⚠️ A CONNECTED DEVICE DOES NOT ADVERTISE (one connection at a time), so a
  // session left open is a device the NEXT scan cannot find (bug, 2026-10-18:
  // "scans once, the second time finds nothing"). The latest session is kept
  // here so leaving the screen can close it — unless it was handed to a device
  // screen (Open full screen), which then owns it and closes it on leaving.
  const sessionRef = useRef(null);
  const handedOverRef = useRef(false);
  // The server's record for the connected board — fetched once on connect, and
  // the normal source of the device TYPE (core/ble/benchType).
  const [serverRow, setServerRow] = useState(null);
  // Why the last Wi-Fi payload could not be built (chooseWifiPayload refusing).
  const payloadErrRef = useRef('');
  // ── Batch mode (owner, 2026-10-18): tick devices, send one command to all ──
  // core/ble/bleBatch + components/BleBatchPanel. In select mode a tap on a row
  // ticks it instead of connecting.
  const [selectMode, setSelectMode] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  // What the SERVER says about each scanned board — the device has no Wi-Fi
  // yet, the phone does. See components/BleServerInfo.
  const { info: serverInfo, refresh: refreshServer } = useServerDetails(devices.map((d) => d.uniqueNumber));
  const [infoUno, setInfoUno] = useState(null);
  // Installation mode: many boards → one location, room + name each, Wi-Fi once.
  const [installMode, setInstallMode] = useState(() => lastScan.installMode);
  const [installRunning, setInstallRunning] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const togglePick = (id) => setPicked((p) => {
    const n = new Set(p);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const [scanning, setScanning] = useState(false);
  const [log, setLog] = useState([]);
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);

  // Remember the list (and mode) for when this screen is opened again.
  useEffect(() => { lastScan = { devices, installMode }; }, [devices, installMode]);

  /*
   * ── Back = one step INSIDE this screen first ─────────────────────────────
   * connected device → the scan list; install mode → the scan list; the
   * server-details dialog → closed. Only from the plain list does back leave
   * BLE Test. While a batch or an install is running, back does nothing: the
   * job is talking to boards one at a time and leaving would orphan it.
   * Both the phone's back key (backIntercept) and the arrow use this.
   */
  const backInsideRef = useRef(() => false);
  backInsideRef.current = () => {
    if (installRunning || batchRunning) return true;
    if (infoUno) { setInfoUno(null); return true; }
    if (session) {
      const s = session;
      setSession(null);
      s.close().catch(() => {});
      return true;
    }
    if (installMode) { setInstallMode(false); return true; }
    if (selectMode) { setSelectMode(false); setPicked(new Set()); return true; }
    return false;
  };
  useEffect(() => interceptBack(() => backInsideRef.current()), []);
  // What the device said it is, and the last status it reported.
  const [identity, setIdentity] = useState(null);   // { deviceId, channels, via }
  const [override, setOverride] = useState(null);   // operator's answer, wins
  const [values, setValues] = useState(null);       // raw channel values
  const [wifiStatus, setWifiStatus] = useState('');
  const [wifiOpen, setWifiOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [advanced, setAdvanced] = useState(false);
  // null until the radio has been asked about — distinct from `false`, so the
  // warning does not flash on every mount before the answer arrives.
  const [btOn, setBtOn] = useState(null);
  // The round trip of the last command: sent → confirmed by a status echo.
  const [ack, setAck] = useState(null);   // { cmd, state, ms, source }
  const ackRef = useRef({ cmd: null, at: 0, timer: null });
  const aliveRef = useRef(true);
  const seqRef = useRef(0);
  // `scan` is defined below `turnOnBluetooth`, which wants to kick one off the
  // moment the radio comes on. A ref rather than a reorder — the definitions are
  // in reading order and that is worth keeping.
  const scanRef = useRef(() => {});
  // Provisioning outcome is decided across two callbacks and a timer, so it is
  // held in refs — a disconnect arrives from bleSession's own closure and would
  // read stale state.
  const wifiRef = useRef({ pending: false, wrote: false, timer: null });
  // ESP32 BLE WWH gateway (type 103): the MESHNET handover, docs/20 §5.
  const [meshStatus, setMeshStatus] = useState('');
  const meshRef = useRef({ pending: false, addr: '', timer: null });

  const add = useCallback((dir, text) => {
    seqRef.current += 1;
    const stamp = new Date().toTimeString().slice(0, 8);
    setLog((prev) => [{ id: seqRef.current, dir, text, stamp }, ...prev].slice(0, 300));
  }, []);

  // ⚠️ SET ON MOUNT, not just cleared on unmount. React 18 StrictMode runs
  // effects mount → unmount → mount, so a ref only ever cleared stays false for
  // the life of the screen — and every `if (!aliveRef.current) return` then
  // silently drops its result. The screen scans, finds nothing, connects to
  // nothing, and reports no error, which is indistinguishable from dead
  // hardware. index.js does not use StrictMode today; this costs one line and
  // stops the day it does from being a two-hour hunt.
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      // Leaving the screen: drop our link so the device advertises again —
      // unless a device screen took it over.
      if (sessionRef.current && !handedOverRef.current) {
        // ⚠️ UNPIN FIRST. The device was pinned to Bluetooth on connect; leaving
        // the flag set after the link is gone would make its buttons publish
        // into a closed session instead of MQTT — a switch that silently stops
        // responding, which is the exact failure bleForced warns about.
        try { setForcedBle(sessionRef.current.uniqueNumber, false); } catch (e) { /* ignore */ }
        sessionRef.current.close().catch(() => {});
      }
      stopScan();
      clearTimeout(wifiRef.current.timer);
      clearTimeout(ackRef.current.timer);
    };
  }, []);

  // ── Writes the type's own controls made ───────────────────────────────────
  // ⚠️ WITHOUT THIS THE LOG IS MISSING HALF THE TRAFFIC. The curtain's
  // Open/Stop/Close (and every other real control drawn here) publishes through
  // publishCommand -> sendControlOverBle, which this screen never saw — and a
  // gap in a bench log reads as a device that did not answer.
  useEffect(() => onBleTrace((uno, message, ok) => {
    if (!aliveRef.current) return;
    add(ok ? 'tx' : 'err', ok ? `${message}  (control)` : `${message} — not sent`);
  }), [add]);

  // ── Provisioning outcome ──────────────────────────────────────────────────
  // Same three rules as core/ble/provisionWifi, because they are the firmware's
  // and not this screen's: a "SAVED" notification is success, a PARSE FAILED /
  // ERROR is failure, and A DROP AFTER THE WRITE IS SUCCESS — the device reboots
  // into gateway mode the moment credentials land, usually before its own reply
  // is delivered. Reporting that as an error fails the happy path.
  const settleWifi = useCallback((ok, message) => {
    if (!wifiRef.current.pending) return;
    clearTimeout(wifiRef.current.timer);
    wifiRef.current = { pending: false, wrote: false, timer: null };
    if (!aliveRef.current) return;
    setWifiStatus(message);
    add(ok ? 'info' : 'err', message);
  }, [add]);

  // The maths is checked here, on the phone, against the spec's own vectors.
  // If this says FAIL, nothing else on this screen is worth trying — and it
  // rules the app out in one glance when a device rejects the key.
  const vectorsOk = selfTest();

  // ── The radio, watched rather than checked once ──────────────────────────
  //
  // A one-off check on mount would be stale the moment the user turns Bluetooth
  // on from the quick settings — which is exactly what they do after reading
  // the warning. The subscription clears the banner by itself, so nobody has to
  // discover that the screen needs a re-scan to notice.
  useEffect(() => {
    const off = watchBleEnabled((on) => {
      if (aliveRef.current) setBtOn(on);
    });
    return off;
  }, []);

  // Bring up Android's own "allow this app to turn on Bluetooth" dialog. On iOS
  // no app may do this, so the button is not offered there — see requestBleEnable.
  const turnOnBluetooth = async () => {
    const on = await requestBleEnable();
    setBtOn(on);
    add(on ? 'info' : 'err', on ? 'Bluetooth turned on' : 'Bluetooth was not turned on');
    if (on) scanRef.current();
  };

  const scan = async () => {
    // ⚠️ THE CHECK THAT WAS MISSING, AND IT COST A DAY OF FIRMWARE HUNTING.
    // With the adapter off, requestLEScan resolves normally and the callback is
    // never invoked, so this ran its full 15-second timeout to an empty list —
    // identical on screen to "no devices are advertising". ensureBleReady now
    // throws inside scanForDevices; this is here so the reason is stated before
    // the wait rather than after it.
    if (!(await isBleEnabled())) {
      setBtOn(false);
      add('err', 'Bluetooth is switched off — a scan would find nothing and report nothing');
      return;
    }
    // A device still connected to THIS phone is not advertising — drop any link
    // left over (an old BLE Test session, a full screen's control link) first.
    if (sessionRef.current && !sessionRef.current.isClosed()) {
      await sessionRef.current.close().catch(() => {});
      add('info', 'closed the open Bluetooth link so that device advertises again');
    }
    sessionRef.current = null;
    await closeControlLink().catch(() => {});
    setDevices([]);
    setScanning(true);
    add('info', 'scanning (active, filtered on service 00FF)…');
    try {
      await scanForDevices((d) => {
        if (!aliveRef.current) return;
        // MERGE, don't ignore. scanForDevices emits a device a second time if
        // its name only arrives after the first sighting; ignoring the repeat
        // would pin the list to the nameless version and leave uniqueNumber
        // null, which is what the whole screen keys off.
        setDevices((prev) => {
          const i = prev.findIndex((x) => x.deviceId === d.deviceId);
          if (i < 0) return [...prev, d];
          const next = prev.slice();
          next[i] = { ...next[i], ...d };
          return next;
        });
        add('info', `found ${d.name || '(no name)'} rssi=${d.rssi ?? 'n/a'}${d.service ? ` state=${d.service.state} tag=${d.service.ownerTag}` : ' (no service data)'}`);
      });
    } catch (e) {
      add('err', (e && e.message) || 'scan failed');
    } finally {
      if (aliveRef.current) setScanning(false);
    }
  };
  scanRef.current = scan;

  const connectTo = async (d) => {
    // ⚠️ LOGGED BEFORE ANY await, AND THAT IS THE POINT. Everything after this
    // line can stall — stopScan, the connect, the handshake — and until this
    // existed a stall produced an empty log, which is indistinguishable from
    // the tap never having registered. One is an app bug, the other is a user
    // looking at the wrong row; they need to be told apart from the log alone.
    add('info', `tapped ${d.name || d.deviceId} — starting`);
    if (!d.uniqueNumber) {
      add('err', `${d.name}: could not parse a unique number from the name — the auth key derives from it, so this device cannot be authenticated`);
      return;
    }
    setBusy(true);
    try {
      await runConnect(d);
    } catch (e) {
      // openSession swallows its own errors, so reaching here means something
      // outside it threw — which must still be visible and must still release
      // the list.
      add('err', (e && e.message) || 'connect failed');
    } finally {
      setBusy(false);
    }
  };

  const runConnect = async (d) => {
    await stopScan();
    // A new device is a new identity — nothing learned from the last one applies,
    // and a stale panel would show the previous device's channels.
    setIdentity(null);
    setOverride(null);
    setValues(null);
    setWifiStatus('');
    setAck(null);
    clearTimeout(ackRef.current.timer);
    ackRef.current = { cmd: null, at: 0, timer: null };
    add('info', `device_key(${d.uniqueNumber}) = ${toHex(deviceKey(d.uniqueNumber, DEFAULT_KEY_VERSION))}`);
    const opened = await openSession(d.deviceId, d.uniqueNumber, {
      // ⚠️ THE TRACE IS THE ONLY LOGGER HERE, and it has to be. bleSession calls
      // trace('rx', …) for EVERY notification and THEN hands the post-handshake
      // ones to onStatus as well, so logging in both printed each status twice —
      // once annotated, once plain — which reads as the device having sent it
      // twice. Annotate in the trace; leave onStatus for state, not output.
      onTrace: (dir, text) => {
        // A MESHNET command carries the location's mesh keys: never shown.
        if (dir !== 'rx') { add(dir, maskMeshNet(text)); return; }
        const s = parseStatus(text);
        add('rx', s
          ? `${text}   → ${s.values.length} ch, source=${s.source || '—'}`
          : text);
      },
      // STATE, not output — the trace above already printed this notification.
      // Logging here as well printed every status twice, which reads as the
      // device having sent it twice.
      onStatus: (text) => {
        // Into the app's message stream too, so a device screen opened from here
        // (Open full screen) — and the Message Box — see this status. Before the
        // alive check: the session outlives this screen once it is handed over.
        relayBleStatus(d.uniqueNumber, text);
        if (!aliveRef.current) return;
        if (meshRef.current.pending && (isMeshNetOk(text) || isMeshNetRejected(text))) {
          clearTimeout(meshRef.current.timer);
          meshRef.current.pending = false;
          setMeshStatus(isMeshNetOk(text)
            ? `✓ The gateway joined this location's mesh as ${meshRef.current.addr}. Test it from the MQTT console: MESHPING.`
            : `✗ The gateway refused the keys: ${text}`);
        }
        if (wifiRef.current.pending) {
          if (isProvisionSuccess(text)) {
            settleWifi(true, 'Wi-Fi saved. The device is restarting and will join your network.');
          } else if (isProvisionFailure(text)) {
            settleWifi(false, `Device rejected the credentials: ${text}`);
          }
        }
        // A status frame is the ONLY evidence the MCU acted. Matched against
        // the command still in flight, so the screen can say "1-1 confirmed in
        // 84 ms" rather than leaving the operator to correlate two log lines by
        // eye. `source` distinguishes our own command (#BLE) from someone
        // pressing the physical panel at the same moment (#Touch).
        const st = parseStatus(text);
        if (st && ackRef.current.cmd) {
          const { cmd, at, timer } = ackRef.current;
          clearTimeout(timer);
          ackRef.current = { cmd: null, at: 0, timer: null };
          setAck({
            cmd, state: 'confirmed', ms: Date.now() - at, source: st.source || '—',
          });
        }

        const learned = learnFromNotification(text);
        if (!learned) return;
        if (learned.values) setValues(learned.values);
        // Ranked, never last-wins: INFO beats a stated type beats a channel
        // count. Status frames arrive continuously, so without this the first
        // one after INFO would downgrade "the device says it is a 49" to "it
        // has four channels, so probably a 49" — and take the channel count
        // from the table instead of from the device.
        setIdentity((prev) => (
          prev && identityRank(prev.via) > identityRank(learned.via) ? prev : learned
        ));
      },
      onDisconnect: () => {
        setSession(null);
        setServerRow(null);
        add('info', 'disconnected');
        // Expected on a successful provision — but ONLY once the write actually
        // went out. A drop before that is a link that failed, not a device that
        // rebooted, and calling it success would report a provision that never
        // happened.
        if (wifiRef.current.pending) {
          settleWifi(
            wifiRef.current.wrote,
            wifiRef.current.wrote
              ? 'Link dropped after the write — the device saved the credentials and rebooted.'
              : 'The device disconnected before Wi-Fi could be saved.',
          );
        }
      },
    });
    if (!aliveRef.current) return;
    if (opened.ok) {
      setSession(opened.session);
      sessionRef.current = opened.session;
      handedOverRef.current = false;
      add('info', `AUTHENTICATED — device is ${opened.session.assigned ? 'ASSIGNED' : 'unassigned'}`);
      // ── WHAT IS IT? ASK THE SERVER, AT ONCE (2026-10-25) ─────────────────
      // One call, and it answers for any unique number whether or not anybody
      // owns it. It is what makes the type's REAL controls possible: the INFO
      // reply names a type the panel may have no branch for (a Curtain
      // Controller showed no curtain buttons), while the record says 13.
      // ⚠️ AND THE DEVICE IS PINNED TO THIS LINK while the screen is open, so
      // the controls that publish through publishCommand go out over Bluetooth
      // instead of MQTT (bleForced + the adopted session). Dropped again on
      // teardown unless a full screen took the link over.
      const uno = opened.session.uniqueNumber;
      try {
        await adoptControlLink(uno, opened.session);
        setForcedBle(uno, true);
      } catch (e) { /* control still works through this screen's own path */ }
      getDeviceDetailFresh(uno)
        .then((res) => {
          const row = Array.isArray(res?.data) ? res.data[0] : null;
          if (!aliveRef.current) return;
          setServerRow(row || null);
          add('info', row
            ? `server: ${row.DeviceName || '(no name)'} — DeviceID ${row.DeviceID}`
              + `${Number(row.LocationID) ? ` · location ${row.LocationID}` : ' · unassigned'}`
            : `server: no record for ${uno} — the type is whatever the device says`);
        })
        .catch(() => { if (aliveRef.current) add('err', 'server: could not read the device record'); });
      // The ONLY traffic this screen sends on its own, and it is what makes the
      // panel possible: INFO may name the device type, and STATUS always gives
      // the channel count. Both are traced like any other write, so the log
      // still shows every byte that left the phone.
      try {
        await opened.session.send(CMD_INFO);
        await opened.session.send(CMD_STATUS);
      } catch (e) {
        add('err', (e && e.message) || 'identify failed');
      }
    } else {
      add('err', `${opened.code}: ${opened.error}`);
    }
  };

  // Arm the round-trip watch. Cleared by a status frame, or fired as a failure.
  const armAck = useCallback((cmd) => {
    clearTimeout(ackRef.current.timer);
    ackRef.current = {
      cmd,
      at: Date.now(),
      timer: setTimeout(() => {
        ackRef.current = { cmd: null, at: 0, timer: null };
        if (!aliveRef.current) return;
        setAck({ cmd, state: 'no-echo' });
        add('err', `${cmd}: written, but no status came back — the device took the `
          + 'bytes and the MCU never reported a new state');
      }, ACK_TIMEOUT_MS),
    };
    setAck({ cmd, state: 'sent' });
  }, [add]);

  const probe = async (cmd) => {
    if (!session) return;
    try {
      await session.send(cmd);
      // AFTER the write resolves, not before: until it does, the bytes have
      // not left and a timeout would be timing the wrong thing.
      if (expectsStatus(cmd)) armAck(cmd);
    } catch (e) {
      clearTimeout(ackRef.current.timer);
      ackRef.current = { cmd: null, at: 0, timer: null };
      setAck({ cmd, state: 'write-failed' });
      add('err', (e && e.message) || 'write failed');
    }
  };

  const sendWifi = async (payload, ssid) => {
    if (!session) return;
    // Armed BEFORE the write, not after: the device answers in milliseconds and
    // a reply that lands first would be dropped by the guard in onStatus.
    clearTimeout(wifiRef.current.timer);
    wifiRef.current = {
      pending: true,
      wrote: false,
      timer: setTimeout(
        () => settleWifi(false, 'No reply from the device. Move closer and try again.'),
        WIFI_REPLY_TIMEOUT_MS,
      ),
    };
    setWifiStatus(`Sending credentials for “${ssid}”…`);
    try {
      await session.send(payload);
      wifiRef.current.wrote = true;
      setWifiStatus('Waiting for the device to confirm…');
    } catch (e) {
      settleWifi(false, (e && e.message) || 'Bluetooth error');
    }
  };

  // The device's answer, unless the operator has overruled it.
  //
  // Channel count, in order: what the device REPORTED in its INFO line, else
  // the table entry for the type, else whatever the last frame was wide enough
  // to show. Never straight from the newest status — a truncated frame would
  // shrink the panel under the operator's fingers mid-test.
  // ⚠️ THE SERVER OUTRANKS THE INFO REPLY (core/ble/benchType): the record is
  // what every other screen draws from, so the bench matching it is the point.
  // A hand-picked override still wins, and a disagreement is reported.
  const benchType = pickBenchType({
    override,
    serverDeviceId: serverRow?.DeviceID,
    deviceDeviceId: identity?.deviceId,
  });
  const shownId = benchType.deviceId;
  const deviceSaidChannels = override == null && identity?.via === 'info'
    ? identity.channels
    : null;
  const shownChannels = shownId == null
    ? null
    : deviceSaidChannels ?? channelCountFor(shownId) ?? identity?.channels ?? null;

  const colourFor = (dir) => (
    dir === 'tx' ? '#4FC3F7' : dir === 'rx' ? '#81C784' : dir === 'err' ? '#EF5350' : `${c.text}99`
  );

  if (!bleSupported()) {
    return (
      <Box sx={{ height: '100%', bgcolor: c.back }}>
        <CenterTitleBar title="BLE Test" showBack />
        <Typography sx={{ color: c.text, p: 3, textAlign: 'center', maxWidth: 520, mx: 'auto' }}>
          Bluetooth only exists in the installed app, not the desktop browser.
        </Typography>
      </Box>
    );
  }

  return (
    // Capped and centred: this screen is phone-shaped, and the desktop shell
    // renders it in a window several times wider. Stretching the log to 1900px
    // makes a 40-character line unreadable.
    // ── ONE SCROLL, and the panel is never the thing that shrinks ───────────
    //
    // This used to be a fixed-height column: the panel was capped at 62% and the
    // log took the rest, each with its own scrollbar. That kept everything on
    // one screen, and it paid for it in the only currency that matters here —
    // the buttons came out smaller than the buttons the customer taps, so the
    // bench and the product no longer looked like the same app.
    //
    // Now the page scrolls. The panel is drawn at TouchScreen's own size and
    // everything else lives below it. The log is newest-first, so the line that
    // matters is directly under the panel and the scroll is rarely needed.
    <Box sx={{
      minHeight: '100%',
      width: '100%',
      maxWidth: 720,
      mx: 'auto',
      bgcolor: c.back,
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
    }}
    >
      {/* ⋮ — everything that is not a control. Wi-Fi provisioning, the device's
          identity, the diagnostics and Disconnect all live here, so what is left
          under it is the panel and nothing else. */}
      <CenterTitleBar
        title="BLE Test"
        showBack
        onBack={() => { if (!backInsideRef.current()) navigate(-1); }}
        actions={(
          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ color: c.text }}>
            <MoreVertIcon />
          </IconButton>
        )}
      />

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        {session && identity && (
          <MenuItem disabled sx={{ opacity: '1 !important' }}>
            <ListItemText
              primary={`${deviceLabel(shownId)} · ${shownChannels} ch`}
              // Which of the three sources decided the type, and whether the
              // server and the device disagree (core/ble/benchType).
              secondary={`${benchTypeNote(benchType, { typeName: serverRow?.DeviceTypeName })}`
                + `${identity ? ` · ${identityLabel(identity)}` : ''}`}
              primaryTypographyProps={{ fontSize: 13, fontWeight: 700 }}
              secondaryTypographyProps={{ fontSize: 11 }}
            />
          </MenuItem>
        )}
        {session && <Divider />}
        <MenuItem
          disabled={!session}
          onClick={() => { setMenuAnchor(null); setWifiOpen(true); }}
        >
          <ListItemIcon><WifiIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary="Add Wi-Fi"
            secondary="Send credentials over Bluetooth"
            secondaryTypographyProps={{ fontSize: 11, opacity: 0.7 }}
          />
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); setAdvanced((v) => !v); }}>
          <ListItemIcon><TuneIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={advanced ? 'Hide diagnostics' : 'Diagnostics'}
            secondary="Raw commands and the device-type override"
            secondaryTypographyProps={{ fontSize: 11, opacity: 0.7 }}
          />
        </MenuItem>
        {session && (
          <MenuItem
            onClick={async () => {
              setMenuAnchor(null);
              await session.close();
              setSession(null);
            }}
          >
            <ListItemIcon><LinkOffIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Disconnect" />
          </MenuItem>
        )}
      </Menu>

      {/* Above everything, including the scan button — with the radio off,
          nothing below this can work and the Scan button is a trap. `false`
          only, not falsy: `null` means "not asked yet". */}
      {btOn === false && (
        <BluetoothOffBanner onTurnOn={turnOnBluetooth} onOpenSettings={openBleSettings} />
      )}

      <Box sx={{ px: 1.5, pt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {/* The auth-vector chip is a bring-up check: it answers "is the app's
            crypto correct" and stops being a question the moment a device has
            authenticated. Once linked it only takes up the row. */}
        {!session && (
          <Chip
            size="small"
            label={vectorsOk ? 'auth vectors OK' : 'AUTH VECTORS FAIL'}
            color={vectorsOk ? 'success' : 'error'}
            sx={{ height: 20, fontSize: 11 }}
          />
        )}
        <Chip
          size="small"
          label={session ? `linked ${session.uniqueNumber}` : 'not linked'}
          color={session ? 'success' : 'default'}
          variant={session ? 'filled' : 'outlined'}
          sx={{ height: 20, fontSize: 11, color: session ? undefined : c.text }}
        />
        <Box sx={{ flex: 1 }} />
        {!session && (
          <Button size="small" onClick={scan} disabled={scanning || busy || installRunning} sx={{ color: c.text, fontSize: 11 }}>
            {scanning ? 'Scanning…' : 'Scan'}
          </Button>
        )}
      </Box>

      {!session && (
        <Box sx={installMode ? { px: 1 } : { maxHeight: '38%', overflowY: 'auto', px: 1 }}>
          {/* ⚠️ SAYING SO OUT LOUD, because a found device does nothing on its
              own and the list read as a status readout rather than a control.
              Scanning is only step one — the connect, the handshake and the
              buttons all wait behind a tap on the row. */}
          {(devices.length > 0 || installMode) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, pt: 0.5, flexWrap: 'wrap' }}>
              <Typography sx={{ color: c.text, fontSize: 12, fontWeight: 700, flex: 1 }}>
                {installMode ? `Installation — ${devices.length} found` : selectMode
                  ? `${picked.size} of ${devices.length} selected — tap to tick`
                  : 'Tap a device to connect →'}
              </Typography>
              {selectMode && (
                <>
                  <Button
                    size="small" disabled={batchRunning}
                    onClick={() => setPicked(new Set(devices.filter((d) => d.uniqueNumber).map((d) => d.deviceId)))}
                    sx={{ color: c.text, textTransform: 'none', minWidth: 0 }}
                  >
                    Select all
                  </Button>
                  <Button
                    size="small" disabled={batchRunning} onClick={() => setPicked(new Set())}
                    sx={{ color: c.text, textTransform: 'none', minWidth: 0 }}
                  >
                    Select none
                  </Button>
                </>
              )}
              <Button
                size="small" variant={installMode ? 'contained' : 'outlined'} disabled={batchRunning || busy || installRunning}
                onClick={() => { setInstallMode((v) => !v); setSelectMode(false); setPicked(new Set()); }}
                sx={{
                  textTransform: 'none',
                  ...(installMode ? { bgcolor: c.button, color: '#fff' } : { color: c.text, borderColor: `${c.text}66` }),
                }}
              >
                {installMode ? 'Close install' : 'Install'}
              </Button>
              {!installMode && (
              <Button
                size="small" variant={selectMode ? 'contained' : 'outlined'} disabled={batchRunning || busy}
                onClick={() => { setSelectMode((v) => !v); setPicked(new Set()); }}
                sx={{
                  textTransform: 'none',
                  ...(selectMode ? { bgcolor: c.button, color: '#fff' } : { color: c.text, borderColor: `${c.text}66` }),
                }}
              >
                {selectMode ? 'Done' : 'Select'}
              </Button>
              )}
            </Box>
          )}
          {installMode && (
            <BleInstallPanel
              devices={devices}
              serverInfo={serverInfo}
              refreshServer={refreshServer}
              mode="admin"
              onRunning={setInstallRunning}
            />
          )}
          {!installMode && (
          <List dense disablePadding>
            {devices.map((d) => (
              <ListItemButton
                key={d.deviceId}
                onClick={() => (selectMode ? (d.uniqueNumber && togglePick(d.deviceId)) : connectTo(d))}
                disabled={busy || batchRunning || (selectMode && !d.uniqueNumber)}
                sx={{
                  // Drawn as a raised control, not a line of text: the tile
                  // colour, a border and a chevron all say "this is a button".
                  bgcolor: `${c.button}22`,
                  border: `1px solid ${c.text}44`,
                  borderRadius: '12px',
                  my: 0.5,
                }}
              >
                {selectMode && (
                  <Checkbox
                    size="small" checked={picked.has(d.deviceId)} tabIndex={-1} disableRipple
                    sx={{ color: c.text, '&.Mui-checked': { color: c.text }, p: 0.5, mr: 0.5 }}
                  />
                )}
                <ListItemText
                  primary={d.uniqueNumber || d.name}
                  secondary={(
                    <>
                      {/* The server's answer first — it is what decides "assign it". */}
                      {d.uniqueNumber && (
                        <Box component="span" sx={{ display: 'block', fontWeight: 600, color: serverInfo[d.uniqueNumber]?.found && !serverInfo[d.uniqueNumber]?.assigned ? 'success.main' : undefined }}>
                          {serverSummary(serverInfo[d.uniqueNumber])}
                        </Box>
                      )}
                      {[
                        /* ⚠️ NOT the server assignment (that is the line above). It is
                           the FIRMWARE's Bluetooth owner-key byte, set only by CLAIM:/
                           OWNER:, which the app has never sent — so every board reads
                           "no key" whoever owns it. Labelled "ble: unassigned" until
                           2026-10-18, which read as a contradiction of the server. */
                        d.service ? (d.service.assigned ? `BT owner key: set (${d.service.ownerTag})` : 'BT owner key: none') : 'no service data',
                        d.rssi == null ? 'signal n/a' : `${d.rssi} dBm`,
                      ].join(' · ')}
                    </>
                  )}
                  primaryTypographyProps={{ sx: { color: c.text, fontWeight: 700, ...MONO } }}
                  secondaryTypographyProps={{ sx: { color: `${c.text}99`, fontSize: 11 } }}
                />
                {!selectMode && d.uniqueNumber && (
                  <IconButton
                    size="small" aria-label="Server details"
                    onClick={(e) => { e.stopPropagation(); setInfoUno(d.uniqueNumber); }}
                    sx={{ color: c.text, mr: 0.5 }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
                {!selectMode && (
                  <Chip
                    size="small"
                    label="Connect"
                    sx={{ height: 22, fontSize: 11, bgcolor: c.button, color: '#fff' }}
                  />
                )}
              </ListItemButton>
            ))}
          </List>
          )}
          <BleServerInfoDialog
            uno={infoUno}
            result={infoUno ? serverInfo[infoUno] : null}
            onClose={() => setInfoUno(null)}
            onRefresh={refreshServer}
          />
          {selectMode && devices.length > 0 && (
            <BleBatchPanel
              selected={devices.filter((d) => picked.has(d.deviceId))}
              onRunning={setBatchRunning}
            />
          )}
          {!scanning && devices.length === 0 && !installMode && (
            <Typography sx={{ color: `${c.text}99`, fontSize: 12, p: 1.5, textAlign: 'center' }}>
              Nothing advertising service 00FF. If firmware BLE is compiled out,
              this is expected.
            </Typography>
          )}
        </Box>
      )}

      {session && (
        // ⚠️ NO HEIGHT CAP. It had `maxHeight: '62%'` with its own scroll, which
        // is what forced the panel to draw itself small. The page scrolls
        // instead — see the note on the root element.
        <Box sx={{ flexShrink: 0 }}>
          {/* ── The REGULAR screen, over this Bluetooth link (2026-10-18) ──────
              Same UI as on the cloud — the type's own controls and the full ⋮
              menu (Settings, Device Info, Add / Remove Wi-Fi, Share QR,
              Unassign…). Commands go over this link (bleForced + the adopted
              session); the device's status comes back through relayBleStatus.
              Server actions (rename, share, unassign) still need internet. A
              board with no database record has no screen to open — the panel
              below stays the way to drive it. */}
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
            <Button
              variant="contained" disabled={opening || busy}
              startIcon={opening ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
              onClick={async () => {
                const uno = session.uniqueNumber;
                setOpening(true);
                try {
                  const res = await getDeviceDetailFresh(uno).catch(() => null);
                  const row = Array.isArray(res?.data) ? res.data[0] : null;
                  if (!row) {
                    add('err', `${uno} has no record in the database — the full screen needs one. Use the panel below.`);
                    return;
                  }
                  await adoptControlLink(uno, session);
                  handedOverRef.current = true;
                  setForcedBle(uno, true);
                  add('info', `opening the full screen for ${uno} over Bluetooth`);
                  // Back from the device screen returns HERE (deviceNav `returnTo`),
                  // not to the device's room.
                  navigate(`/device/${uno}`, { state: { device: row, from: { returnTo: '/ble-test' } } });
                } finally {
                  if (aliveRef.current) setOpening(false);
                }
              }}
              sx={{ bgcolor: c.button, color: '#fff', textTransform: 'none', fontWeight: 700, borderRadius: '12px' }}
            >
              Open full screen
            </Button>
          </Box>
          {shownId != null && shownChannels > 0 && (
            <BleDevicePanel
              deviceId={shownId}
              uniqueNumber={session.uniqueNumber}
              channels={shownChannels}
              values={values}
              onCommand={probe}
              disabled={busy}
              showHeader={false}
            />
          )}

          {/* ── Did the press reach the relay? ──────────────────────────────
              The one line that answers it, in the two halves it actually has.
              See ACK_TIMEOUT_MS for why the status echo is the only evidence
              of the second hop. */}
          {ack && <AckLine ack={ack} c={c} />}

          {/* Diagnostics, off by default and behind the ⋮ menu — the type
              override and the raw command buttons are bring-up tools, not
              controls, and on a connected device they were the bulk of what
              was on screen. */}
          {advanced && (
          <>
          <Box sx={{ px: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography sx={{ color: `${c.text}99`, fontSize: 11 }}>
              {identity ? identityLabel(identity) : 'type unknown — send INFO or STATUS, or pick one'}
            </Typography>
            {override != null && (
              <Button
                size="small"
                onClick={() => setOverride(null)}
                sx={{ minWidth: 0, fontSize: 10, py: 0, color: c.text }}
              >
                auto
              </Button>
            )}
            {TYPE_CHOICES.map((t) => (
              <Button
                key={t.id}
                size="small"
                variant={shownId === t.id ? 'contained' : 'outlined'}
                onClick={() => setOverride(t.id)}
                sx={{
                  minWidth: 0,
                  fontSize: 10,
                  py: 0,
                  px: 0.75,
                  color: shownId === t.id ? '#fff' : c.text,
                  bgcolor: shownId === t.id ? c.button : 'transparent',
                  borderColor: `${c.text}55`,
                }}
              >
                {t.label}
              </Button>
            ))}
          </Box>

          <Box sx={{ px: 1, py: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {PROBES.map((cmd) => (
              <Button
                key={cmd}
                size="small"
                variant="outlined"
                onClick={() => probe(cmd)}
                sx={{ minWidth: 0, fontSize: 11, py: 0.1, color: c.text, borderColor: `${c.text}55` }}
              >
                {cmd}
              </Button>
            ))}
          </Box>
          </>
          )}

          {/* ESP32 BLE WWH gateway: give it this location's mesh keys and its
              OWN controller address (docs/20 §5). Shown for type 103, or with
              the advanced tools on. */}
          {(identity?.deviceId === GATEWAY_DEVICE_ID || advanced) && (
            <Box sx={{ px: 1, py: 0.5 }}>
              <Button
                size="small"
                variant="contained"
                disabled={busy || meshRef.current.pending}
                onClick={async () => {
                  if (!session) return;
                  setMeshStatus('Getting this location\'s mesh keys…');
                  try {
                    const { network, nodeName } = await fetchGatewayNetwork(userId, session.uniqueNumber);
                    const cmd = meshNetCommand(network);
                    const addr = cmd.slice(cmd.lastIndexOf(',') + 1);
                    clearTimeout(meshRef.current.timer);
                    meshRef.current = {
                      pending: true,
                      addr,
                      timer: setTimeout(() => {
                        if (!meshRef.current.pending) return;
                        meshRef.current.pending = false;
                        setMeshStatus('No reply from the gateway. Check MESHPING from the MQTT console — it may still have joined.');
                      }, 10000),
                    };
                    await session.send(cmd);
                    setMeshStatus(`Sent (network of "${nodeName}", gateway address ${addr}). Waiting for the gateway…`);
                  } catch (e) {
                    meshRef.current.pending = false;
                    setMeshStatus(`✗ ${(e && e.message) || e}`);
                  }
                }}
                sx={{ bgcolor: c.button, color: c.text, fontSize: 11 }}
              >
                Send mesh keys (gateway)
              </Button>
              {meshStatus && (
                <Typography sx={{ color: `${c.text}cc`, fontSize: 11, mt: 0.5 }}>{meshStatus}</Typography>
              )}
            </Box>
          )}

          {wifiStatus && (
            <Typography sx={{ color: `${c.text}99`, fontSize: 11, px: 1.5, pb: 0.5 }}>
              {wifiStatus}
            </Typography>
          )}
        </Box>
      )}

      {/* ── Add Wi-Fi: THE SAME DIALOG THE DESKTOP USES (2026-10-25) ────────
          Pick a location, get its saved SSIDs *with their passwords* (the
          registered list plus whatever sibling devices reported to getallwifi
          this session), or type a new one — instead of two blank fields on a
          bench where the network the board needs is the customer's, not the
          office's. Only the TRANSPORT differs: `buildPayload` makes it
          chooseWifiPayload's BLE form and `sendAddressed` puts it on the open
          link through this screen's own logged path.
          ⚠️ Registering the network to the location still waits for the device
          to be HEARD on MQTT — see the dialog's own note. */}
      {session && (
        <AddWifiDialog
          open={wifiOpen}
          uno={session.uniqueNumber}
          subtitle={`over Bluetooth · ${wifiStatus || 'the link is open'}`}
          onClose={() => setWifiOpen(false)}
          buildPayload={(ssid, pass) => {
            // ⚠️ IT CAN REFUSE: an SSID containing both ',' and '&' cannot be
            // carried by either form, and that refusal is the firmware's, not a
            // validation whim (bleConfig). Keep the reason for the log.
            const r = chooseWifiPayload(ssid, pass);
            payloadErrRef.current = r.ok ? '' : (r.error || 'these credentials cannot be sent over Bluetooth');
            return r.ok ? r.payload : '';
          }}
          sendAddressed={(payload) => {
            if (!payload) {
              add('err', payloadErrRef.current || 'these credentials cannot be sent over Bluetooth');
              setWifiStatus(payloadErrRef.current || 'not sent');
              return;
            }
            // The dialog hands over the finished payload; the SSID is only for
            // the status line, so it is read back out of what we are sending.
            const shown = /^WIFI:/i.test(payload)
              ? payload.slice(5).split(',')[0]
              : ((/ssid=([^&]*)/i.exec(payload) || [])[1] || '(unknown)');
            sendWifi(payload, shown);
          }}
        />
      )}

      {/* The raw typed form stays, under Diagnostics: an SSID the estate has
          never seen is a real bench case, and so is testing the OTHER payload
          form by hand. */}
      {advanced && session && (
        <Box sx={{ px: 1, pb: 1 }}>
          <BleWifiForm
            onSend={(payload, ssid) => sendWifi(payload, ssid)}
            disabled={busy || !session}
            status={wifiStatus}
          />
        </Box>
      )}

      <Divider sx={{ borderColor: `${c.text}33` }} />

      <Box sx={{ px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ color: c.text, fontWeight: 700, fontSize: 12, flex: 1 }}>
          Exchange
        </Typography>
        <Button
          size="small"
          onClick={() => copyText(log.map((l) => `${l.stamp} ${l.dir} ${l.text}`).join('\n'))}
          disabled={log.length === 0}
          sx={{ color: c.text, fontSize: 11 }}
        >
          Copy
        </Button>
        <Button size="small" onClick={() => setLog([])} sx={{ color: c.text, fontSize: 11 }}>
          Clear
        </Button>
      </Box>

      {/* Newest-first and in normal flow — no inner scrollbar. The line that
          just arrived is at the top, immediately under the Exchange header, so
          the page scroll is what moves and there is only ever one of them. */}
      <Box sx={{ px: 1, pb: 1 }}>
        {busy && <CircularProgress size={16} sx={{ color: c.text, m: 1 }} />}
        {log.map((l) => (
          <Box key={l.id} sx={{ ...MONO, color: colourFor(l.dir), py: '1px', wordBreak: 'break-all' }}>
            <Box component="span" sx={{ color: `${c.text}66`, mr: 0.5 }}>{l.stamp}</Box>
            <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
              {l.dir === 'tx' ? '→' : l.dir === 'rx' ? '←' : l.dir === 'err' ? '!' : '·'}
            </Box>
            {l.text}
          </Box>
        ))}
        {log.length === 0 && (
          <Typography sx={{ color: `${c.text}66`, fontSize: 12, p: 1.5, textAlign: 'center' }}>
            Scan, then tap a device. Every byte in and out appears here.
          </Typography>
        )}
      </Box>

      <Box sx={{ px: 1.5, py: 0.5 }}>
        <Typography sx={{ color: `${c.text}66`, fontSize: 10 }}>
          {`4-touch = ${channelsFor(49)} channels · PING works before auth, so if PING answers and AUTH does not, the radio is fine and the key is wrong.`}
        </Typography>
      </Box>
    </Box>
  );
}
